/**
 * CPF Update Modal
 * Modal que aparece para usuários legados (que fizeram login com email) 
 * adicionarem seu CPF à conta
 */
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertCircle, CheckCircle2, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';

// CPF validation helper
const validateCPF = (cpf) => {
  const cleanCPF = cpf.replace(/\D/g, '');
  if (cleanCPF.length !== 11) return false;
  
  // Check for known invalid patterns
  if (/^(\d)\1+$/.test(cleanCPF)) return false;
  
  // Validate check digits
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.charAt(9))) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.charAt(10))) return false;
  
  return true;
};

// Format CPF helper
const formatCPF = (value) => {
  const numbers = value.replace(/\D/g, '');
  return numbers
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .slice(0, 14);
};

export default function CPFUpdateModal() {
  const { user, updateCPF } = useAuth();
  const [open, setOpen] = useState(true);
  const [cpf, setCpf] = useState('');
  const [saving, setSaving] = useState(false);
  const [cpfError, setCpfError] = useState('');

  // Only show if user needs CPF update
  if (!user?.needs_cpf_update) {
    return null;
  }

  const handleCPFChange = (e) => {
    const formatted = formatCPF(e.target.value);
    setCpf(formatted);
    
    const clean = formatted.replace(/\D/g, '');
    if (clean.length === 11) {
      if (!validateCPF(clean)) {
        setCpfError('CPF inválido');
      } else {
        setCpfError('');
      }
    } else {
      setCpfError('');
    }
  };

  const handleSubmit = async () => {
    const cleanCPF = cpf.replace(/\D/g, '');
    
    if (!validateCPF(cleanCPF)) {
      setCpfError('CPF inválido');
      return;
    }

    setSaving(true);
    try {
      await updateCPF(cleanCPF);
      toast.success('CPF cadastrado com sucesso!');
      setOpen(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao cadastrar CPF';
      toast.error(message);
      if (message.includes('já cadastrado')) {
        setCpfError('Este CPF já está vinculado a outra conta');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    setOpen(false);
    toast.info('Você pode atualizar seu CPF depois nas configurações');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[425px]" data-testid="cpf-update-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-600" />
            Atualize seu Cadastro
          </DialogTitle>
          <DialogDescription>
            Para maior segurança e conformidade com a LGPD, precisamos do seu CPF.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Info Alert */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Por que precisamos do seu CPF?</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Identificação única e segura</li>
                  <li>Conformidade com a LGPD</li>
                  <li>Proteção contra duplicidade de contas</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CPF Input */}
          <div className="space-y-2">
            <Label htmlFor="cpf">CPF</Label>
            <Input
              id="cpf"
              value={cpf}
              onChange={handleCPFChange}
              placeholder="000.000.000-00"
              className={cpfError ? 'border-red-500' : ''}
              data-testid="cpf-update-input"
            />
            {cpfError && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {cpfError}
              </p>
            )}
            {cpf.replace(/\D/g, '').length === 11 && !cpfError && (
              <p className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                CPF válido
              </p>
            )}
          </div>

          {/* Privacy Notice */}
          <p className="text-xs text-slate-500">
            Seu CPF será armazenado de forma segura e não será compartilhado com terceiros 
            sem seu consentimento explícito.
          </p>
        </div>

        <div className="flex gap-3 justify-end">
          <Button 
            variant="ghost" 
            onClick={handleSkip}
            disabled={saving}
          >
            Depois
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={saving || cpf.replace(/\D/g, '').length !== 11 || !!cpfError}
            className="bg-cyan-600 hover:bg-cyan-700"
            data-testid="cpf-update-submit"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              'Cadastrar CPF'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
